"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, Wrench, GraduationCap, Users as Users2, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Microscope } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  id: "consulting",
  title: "Scientific Consulting",
  tag: "Regulatory & Compliance",
  description: "Application-led advisory for analytical method development, workflow qualification, and audit readiness in regulated laboratories.",
  icon: FileText,
  features: ["Method development and validation protocols", "Pharmacopeial compliance and data integrity reviews", "Technology evaluation and instrument selection", "Laboratory workflow optimization audits"],
  link: "/ServicesPage"
}, {
  id: "technical-support",
  title: "Technical Support",
  tag: "Maintenance & Qualification",
  description: "Certified field engineers providing lifecycle maintenance, rapid troubleshooting, and qualification protocols across Ireland and the UK.",
  icon: Wrench,
  features: ["IQ / OQ / PQ validation documentation", "Preventive maintenance and scheduled calibration", "Rapid on-site and remote diagnostic support", "Genuine OEM parts replacement and lifecycle management"],
  link: "/ServicesPage"
}, {
  id: "training",
  title: "Professional Training",
  tag: "Capability Development",
  description: "Structured operator training, software proficiency programs, and advanced application courses for technical laboratory staff.",
  icon: GraduationCap,
  features: ["Hands-on analytical instrument operation", "Data analysis software workflows and reporting", "Good laboratory practice (GLP) refreshers", "Customized on-site team qualification workshops"],
  link: "/ServicesPage"
}, {
  id: "collaboration",
  title: "Academic Collaboration",
  tag: "Joint R&D",
  description: "Partnership programs bridging academic research institutions and industrial laboratories through joint methodology trials.",
  icon: Users2,
  features: ["Pilot feasibility studies and proof-of-concept testing", "Shared instrumentation access programs", "Collaborative grant and research support", "Application notes and joint scientific dissemination"],
  link: "/ContactPage"
}];
export default function ResearchServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="researchsupportservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Header Section */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs tracking-wide uppercase">
            <Microscope className="size-3.5 mr-1 text-primary" />
            Support Ecosystem
          </Badge>
          
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground font-default mb-4">
            Research Support Services
          </h2>
          
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Delivering comprehensive technical support, method advisory, operator training, and collaborative programs for laboratories across Ireland and the UK.
          </p>

          <div className="flex items-center justify-center gap-6 mt-6 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="size-4 text-primary" /> ISO / GLP Compliant Protocols
            </span>
            <span className="hidden sm:inline-block h-3 w-px bg-border" />
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-primary" /> Certified Service Engineers
            </span>
          </div>
        </div>

        {/* Services Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }}>
          {services.map((service, index) => {
          const IconComponent = service.icon;
          return <motion.div key={service.id} variants={itemVariants} data-index={index}>
                <Card className="h-full flex flex-col justify-between border border-border bg-card hover:border-primary/40 hover:shadow-md transition-all duration-300">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="p-3 rounded-lg bg-secondary text-secondary-foreground">
                        <IconComponent className="size-6 text-primary" />
                      </div>
                      <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                        {service.tag}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground font-default">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="py-2 flex-1">
                    <div className="h-px w-full bg-border/60 mb-4" />
                    <h3 className="text-xs font-semibold tracking-wider text-muted-foreground uppercase mb-3">
                      Key Deliverables
                    </h3>
                    <ul className="space-y-2.5">
                      {service.features.map((feature, fIndex) => <li key={fIndex} className="flex items-start gap-2.5 text-sm text-card-foreground/90">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="pt-4 mt-auto border-t border-border/60">
                    <Button asChild variant="outline" className="w-full justify-between group hover:border-primary hover:text-primary">
                      <Link to={service.link}>
                        <span>Explore {service.title}</span>
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Banner */}
        <div className="mt-14 rounded-xl border border-border bg-secondary/50 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <h3 className="text-lg font-semibold text-foreground">
              Need custom technical assistance for your laboratory?
            </h3>
            <p className="text-sm text-muted-foreground">
              Speak directly with our application specialists to schedule qualifications or discuss technical projects.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild variant="default" className="w-full sm:w-auto">
              <Link to="/ContactPage">
                Contact Specialist Team
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to="/ServicesPage">
                View All Services
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}